import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, TrendingUp, Clock, CheckCircle, 
  Plus, ArrowRight, Euro
} from 'lucide-react';
import { toast } from 'sonner';
import { getDashboardStats, formatMoney, formatDate, getApiError } from '../api';
import { useLanguage } from '../i18n';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const StatCard = ({ icon: Icon, label, value, subValue, color }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subValue && (
          <p className="text-sm text-gray-400 mt-1">{subValue}</p>
        )}
      </div>
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon size={24} className="text-white" />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const styles = {
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700',
    expired: 'bg-red-100 text-red-700',
  };
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
      {t(`quotes.status.${status}`)}
    </span>
  );
};

const QdDashboard = () => {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadStats();
  }, []);
  
  const loadStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F7D70]" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-gray-500">{t('login.subtitle')}</p>
        </div>
        <Link
          to="/quote/quotes/new"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors"
          data-testid="new-quote-button"
        >
          <Plus size={20} />
          {t('quotes.newQuote')}
        </Link>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={FileText}
          label={t('dashboard.totalQuotes')}
          value={stats?.total_quotes || 0}
          color="bg-[#14233F]"
        />
        <StatCard
          icon={Clock}
          label={t('dashboard.sent')}
          value={stats?.sent_count || 0}
          color="bg-blue-500"
        />
        <StatCard
          icon={CheckCircle}
          label={t('dashboard.accepted')}
          value={stats?.accepted_count || 0}
          color="bg-green-500"
        />
        <StatCard
          icon={Euro}
          label={t('dashboard.totalValue')}
          value={formatMoney(stats?.accepted_value || 0)}
          color="bg-[#0F7D70]"
        />
      </div>
      
      {/* Chart */}
      {stats?.monthly_data && stats.monthly_data.length > 0 && (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.quotesOverTime')}</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.monthly_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                <YAxis stroke="#6B7280" fontSize={12} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="sent" 
                  name={t('dashboard.sent')}
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="accepted" 
                  name={t('dashboard.accepted')}
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ fill: '#10B981' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {/* Recent Quotes */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.recentQuotes')}</h2>
          <Link 
            to="/quote/quotes"
            className="text-sm text-[#0F7D70] hover:underline flex items-center gap-1"
          >
            {t('quotes.title')} <ArrowRight size={16} />
          </Link>
        </div>
        
        {stats?.recent_quotes && stats.recent_quotes.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {stats.recent_quotes.map((quote) => (
              <Link
                key={quote.id}
                to={`/quote/quotes/${quote.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900">{quote.number}</p>
                  <p className="text-sm text-gray-500">{quote.client_snapshot?.name}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">
                    {formatMoney(quote.totals?.total || 0)}
                  </p>
                  <StatusBadge status={quote.status} />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <FileText size={40} className="mx-auto mb-3 opacity-30" />
            <p>{t('dashboard.noRecentQuotes')}</p>
            <Link
              to="/quote/quotes/new"
              className="inline-flex items-center gap-2 mt-3 text-[#0F7D70] hover:underline"
            >
              <Plus size={16} />
              {t('dashboard.createFirst')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default QdDashboard;
